import partition from "lodash/partition";

import { type MessagePathPart, MessagePathPartType } from "./MessagePathRecord";
import {
  type ParsedMessagePathAttr,
  type ParsedMessagePathSlice,
  type PathParseResult,
  PathParser,
  pathParser,
} from "./PathParser";
import { MessagePathNode, TopicNode } from "./TopicTree";

interface PathMatchPart {
  isMatch: boolean;
  substring: string;
}

export interface PathMatch {
  // A match is complete if the topic and each of the message path parts are exact matches
  // and the path under consideration has the same number of message path parts as the option.
  isComplete: boolean;
  match: MessagePathNode;
  parts: PathMatchPart[];
}

export class PathMatcher {
  public findMatches(
    input: PathParseResult,
    matchCandidates: TopicNode[],
    matchFilter?: (messagePath: MessagePathNode) => boolean,
  ): PathMatch[] {
    const matches: PathMatch[] = [];
    for (const topic of matchCandidates) {
      const topicMatches = this.#evaluateMatch(input, topic, matchFilter);
      matches.push(...topicMatches);
    }
    return partition(matches, (m) => m.isComplete).flat();
  }

  /**
   * Finds the message path nodes that a joined "topicName.message.path" string
   * refers to. Nothing in the string marks where the topic name ends and the
   * message path begins, so two resolutions are tried in order:
   *
   * 1. Nodes whose own string form ({@link MessagePathNode.toString}) is the
   *    string. Every one of them is returned, because nodes under differently
   *    named topics can spell themselves the same way: "b.c" under "/a" and "c"
   *    under "/a.b" both spell "/a.b.c". Returning both leaves the choice to the
   *    caller.
   * 2. Splitting the string at each candidate topic name it starts with and
   *    matching the remainder as a message path. This resolves a string no node
   *    spells, such as one carrying narrowed slice bounds ("data[2]") against a
   *    node that spells itself "data[:]". Names are tried longest first, since a
   *    longer name accounts for more of the string, and the first name yielding
   *    complete matches wins.
   */
  public findNodesByJoinedPath(
    topicMessagePath: string,
    candidates: TopicNode[],
  ): MessagePathNode[] {
    const spelledExactly: MessagePathNode[] = [];
    for (const topic of candidates) {
      for (const node of topic) {
        if (node.toString() === topicMessagePath) {
          spelledExactly.push(node);
        }
      }
    }
    if (spelledExactly.length > 0) {
      return spelledExactly;
    }

    const topicNames = Array.from(
      new Set(candidates.map((topic) => topic.name)),
    ).sort((a, b) => b.length - a.length);

    for (const topicName of topicNames) {
      const parsedPath = pathParser.parseWithKnownTopic(
        topicMessagePath,
        topicName,
      );
      if (parsedPath === undefined) {
        continue;
      }

      const matches = this.findMatches(parsedPath, candidates)
        .filter((match) => match.isComplete)
        .map((match) => match.match);

      if (matches.length > 0) {
        return matches;
      }
    }

    return [];
  }

  /**
   * Finds the message path nodes in `candidates` that a topic name and a list
   * of message path parts refer to. Nothing is parsed here: the topic name and
   * the parts arrive as separate values, so a dot inside either one cannot be
   * mistaken for a separator.
   *
   * A node matches when its topic's name equals `topicName` and its parts line
   * up with `parts` one for one: same count, same kinds, same attribute names.
   * Two things do not decide a match:
   *
   * 1. Attribute data types. A caller that needs a particular one reads
   *    `data.canonical_data_type` off the matched node itself.
   * 2. Slice bounds, which select elements within a field rather than identify
   *    a node. The returned node carries the bounds `parts` asked for, whether
   *    narrower or wider than its own; where they differ it is a copy, so the
   *    node in the tree is left untouched.
   *
   * Several candidates can carry `topicName` (the list can span every file open
   * in the workspace), and each contributes its own node, in the order the
   * candidates were given.
   */
  public findNodesByParts(
    topicName: string,
    parts: MessagePathPart[],
    candidates: TopicNode[],
  ): MessagePathNode[] {
    const matches: MessagePathNode[] = [];
    for (const topic of candidates) {
      if (topic.name !== topicName) {
        continue;
      }

      for (const node of topic) {
        const match = this.#matchParts(parts, node);
        if (match !== undefined) {
          matches.push(match);
        }
      }
    }
    return matches;
  }

  /**
   * The matches one topic's nodes offer for `input`. Each splits the
   * candidate's full "topicName.message.path" text into substrings flagged as
   * matched or not, and counts as complete when every one of them, topic name
   * included, is flagged.
   *
   * The input decides how a node is matched:
   *
   * 1. With no message path segments, the input is one fragment sought
   *    anywhere in the topic name and anywhere in the node's attribute names;
   *    the node is offered on the strength of either.
   * 2. With segments, the topic name must be exact, and the segments are
   *    compared position by position against the node's own, which cannot be
   *    fewer.
   *
   * Empty input offers every node with nothing flagged; `matchFilter`
   * withholds the nodes it rejects.
   */
  #evaluateMatch = (
    input: PathParseResult,
    topic: TopicNode,
    matchFilter?: (messagePath: MessagePathNode) => boolean,
  ): PathMatch[] => {
    const isExactTopicMatch = input.topic.name === topic.name;
    const inputHasMessagePathSegments = input.messagePath.length > 0;
    if (!isExactTopicMatch && inputHasMessagePathSegments) {
      // E.g., an input of "topic.message.path" should not match the option "topic100.message.path"
      return [];
    }

    const topicPartMatches = this.#matchText(input.topic.name, topic.name);
    const topicDoesMatch = topicPartMatches.some((match) => match.isMatch);
    const performSubstringMatch = !inputHasMessagePathSegments;

    if (!topicDoesMatch && inputHasMessagePathSegments) {
      return [];
    }

    const topicSeparator = {
      isMatch: inputHasMessagePathSegments,
      substring: PathParser.TOPIC_SEPARATOR,
    };
    topicPartMatches.push(topicSeparator);

    const matches = [];
    for (const messagePath of topic) {
      // Comparing the node in parsed form lets an attribute named with
      // "reserved characters" (e.g., square brackets, as in "ev_hpos[0]") match
      // the input, which the caller has already parsed. The topic name is
      // handed to the parse because toString() spells a node as its topic's
      // name, the separator, then its parts, and the name may itself hold dots.
      const knownPath = messagePath.toString();
      const parsedMessagePath = pathParser.parseWithKnownTopic(
        knownPath,
        topic.name,
      );
      if (parsedMessagePath === undefined) {
        continue;
      }
      if (input.messagePath.length > parsedMessagePath.messagePath.length) {
        continue;
      }

      if (matchFilter !== undefined && !matchFilter(messagePath)) {
        continue;
      }

      const pathMatch = performSubstringMatch
        ? this.#evaluateSubstringMatch(input.topic.name, messagePath)
        : this.#evaluatePositionalMatch(input, parsedMessagePath, messagePath);

      if (pathMatch === null) {
        continue;
      }

      // Insert topic name PathMatchParts at head of the match's parts breakdown
      pathMatch.parts.unshift(...topicPartMatches);

      const anyPartsMatch = pathMatch.parts.some((match) => match.isMatch);
      if (anyPartsMatch || input.isEmpty) {
        pathMatch.isComplete = pathMatch.parts.every((part) => part.isMatch);
        matches.push(pathMatch);
      }
    }

    return matches;
  };

  /**
   * Validates if input path segments match exactly at the same positions in candidate path.
   * For non-final segments, requires exact matches (e.g. "foo.bar.baz" matching "foo.barre.qux").
   * For final segment, allows partial matches (e.g. "foo.ba" matching "foo.bar").
   */
  #evaluatePositionalMatch = (
    input: PathParseResult,
    known: PathParseResult,
    node: MessagePathNode,
  ): PathMatch | null => {
    const pathMatch: PathMatch = {
      isComplete: false,
      match: node,
      parts: [],
    };

    for (const [idx, knownPart] of known.messagePath.entries()) {
      const inputPart = input.messagePath[idx];

      if (knownPart.type === MessagePathPartType.Attr) {
        // Syntactically, an attribute must have been preceded by another match part
        const hasPriorMatchPart = pathMatch.parts.length > 0;
        if (hasPriorMatchPart) {
          pathMatch.parts.push({
            isMatch: true,
            substring: PathParser.MESSAGE_PATH_SEPARATOR,
          });
        }
      }

      if (inputPart === undefined) {
        if (knownPart.type === MessagePathPartType.Attr) {
          pathMatch.parts.push({
            isMatch: false,
            substring: knownPart.value,
          });
        } else {
          if (knownPart.isRange) {
            const end = Number.isFinite(knownPart.end) ? knownPart.end : "";
            const range = `[${knownPart.start}:${end}]`;
            const substring = range === "[0:]" ? "[:]" : range;
            pathMatch.parts.push({
              isMatch: false,
              substring,
            });
          } else {
            pathMatch.parts.push({
              isMatch: false,
              substring: `[${knownPart.start}]`,
            });
          }
        }
        continue;
      }

      if (inputPart.type !== knownPart.type) {
        // type mismatch, early return
        return null;
      }

      if (
        inputPart.type === MessagePathPartType.Slice &&
        knownPart.type === MessagePathPartType.Slice
      ) {
        const slicePartMatches = this.#matchSlice(
          inputPart,
          knownPart,
          pathMatch.match,
          idx,
        );
        if (slicePartMatches.matches.length > 0) {
          pathMatch.parts.push(...slicePartMatches.matches);
          pathMatch.match = slicePartMatches.node;
        } else {
          return null;
        }
      }

      if (
        inputPart.type === MessagePathPartType.Attr &&
        knownPart.type === MessagePathPartType.Attr
      ) {
        const attrPartMatches = this.#matchAttr(inputPart, knownPart);
        if (attrPartMatches.length > 0) {
          pathMatch.parts.push(...attrPartMatches);
        } else {
          return null;
        }
      }
    }

    return pathMatch;
  };

  /**
   * Search for `substring` anywhere within any "Attribute"-type MessagePathParts.
   */
  #evaluateSubstringMatch = (
    substring: string,
    messagePath: MessagePathNode,
  ): PathMatch => {
    const pathMatch: PathMatch = {
      isComplete: false,
      match: messagePath,
      parts: [],
    };

    for (const part of messagePath.toParts()) {
      if (part.type === MessagePathPartType.Attr) {
        if (pathMatch.parts.length > 0) {
          pathMatch.parts.push({
            isMatch: false,
            substring: PathParser.MESSAGE_PATH_SEPARATOR,
          });
        }
        pathMatch.parts.push(...this.#matchText(substring, part.attribute));
      } else {
        pathMatch.parts.push({
          isMatch: false,
          substring: "[:]",
        });
      }
    }
    return pathMatch;
  };

  #matchAttr(
    input: ParsedMessagePathAttr,
    known: ParsedMessagePathAttr,
  ): PathMatchPart[] {
    const matches: PathMatchPart[] = [];

    const isExact = input.value === known.value;
    if (!input.isFinalSegment && !isExact) {
      // If this is an intermediate segment of the input, it must exactly match this attribute.
      // E.g., given the input "foo.bar.baz", if evaluating the "bar" message path part,
      // this should not match the message path "foo.barre.baz"
      return [];
    }

    const attrMatches = this.#matchText(input.value, known.value);
    const anyMatch = attrMatches.some((match) => match.isMatch);
    if (!anyMatch) {
      return [];
    } else {
      matches.push(...attrMatches);
    }

    return matches;
  }

  /**
   * The match for `node` against `parts`, or undefined when the two do not line
   * up. Differing slice bounds still match; the result is then a copy of `node`
   * carrying the bounds `parts` asked for. See {@link findNodesByParts} for the
   * full contract.
   */
  #matchParts = (
    parts: MessagePathPart[],
    node: MessagePathNode,
  ): MessagePathNode | undefined => {
    const nodeParts = node.toParts();
    if (nodeParts.length !== parts.length) {
      return undefined;
    }

    let match = node;
    for (const [index, part] of parts.entries()) {
      const nodePart = nodeParts[index];
      switch (part.type) {
        case MessagePathPartType.Attr:
          if (
            nodePart.type !== MessagePathPartType.Attr ||
            nodePart.attribute !== part.attribute
          ) {
            return undefined;
          }
          break;
        case MessagePathPartType.Slice: {
          if (nodePart.type !== MessagePathPartType.Slice) {
            return undefined;
          }

          // `?? Infinity` puts a stored null and an in-memory Infinity on the
          // same footing, so bounds that differ only in spelling do not copy
          // the node.
          const start = part.start;
          const end = part.end ?? Infinity;
          if (start !== nodePart.start || end !== (nodePart.end ?? Infinity)) {
            match = match.withModifiedSlicePart(index, { start, end });
          }
          break;
        }
      }
    }

    return match;
  };

  #matchSlice(
    input: ParsedMessagePathSlice,
    known: ParsedMessagePathSlice,
    node: MessagePathNode,
    position: number, // index within node.toParts
  ): { matches: PathMatchPart[]; node: MessagePathNode } {
    const ret: { matches: PathMatchPart[]; node: MessagePathNode } = {
      matches: [],
      node,
    };

    if (!input.isValid && !known.isValid) {
      // E.g., a message path is named using the square bracket "reserved characters": "column[abc]"
      const isExact = input.value === known.value;
      if (!isExact && input.isClosed) {
        return ret;
      }

      const stringMatches = this.#matchText(input.value, known.value);
      if (stringMatches.some((match) => match.isMatch)) {
        return {
          matches: stringMatches,
          node,
        };
      }
      return ret;
    }

    if (!input.isValid) {
      // Expecting a number, given something that couldn't be coerced to a number
      return ret;
    }

    if (!known.isRange) {
      if (input.isRange) {
        // E.g., known message path is named "ev_hpos[0]" but input is "ev_hpos[:]"
        return ret;
      }

      if (input.start !== undefined && known.start !== input.start) {
        // E.g., known message path is named "ev_hpos[0]" but input is "ev_hpos[2]"
        return ret;
      }
    }

    ret.matches.push({
      isMatch: true,
      substring: input?.value,
    });

    if (!input.isClosed) {
      if (input.start === undefined) {
        const isFullRange =
          known.isRange && known.start === 0 && !Number.isFinite(known.end);
        const start = isFullRange ? ":" : known.start;
        ret.matches.push({
          isMatch: false,
          substring: `${start}]`,
        });
      } else {
        ret.matches.push({
          isMatch: false,
          substring: "]",
        });
      }
    }

    if (node.toParts()[position]?.type === MessagePathPartType.Slice) {
      // There may be a disconnect between the message path node part and the ParseResult
      // obtained by parsing a message path node's string representation if a message path
      // includes square brackets, which are "reserved characters" parsed by the PathParser.
      let end = Number.isFinite(known.end) ? known.end : input.end;
      if (
        !Number.isFinite(end) &&
        input.start !== undefined &&
        !input.isRange
      ) {
        end = input.start + 1;
      }

      ret.node = node.withModifiedSlicePart(position, {
        start: input.start,
        end,
      });
    }

    return ret;
  }

  #matchText(input: string, known: string): PathMatchPart[] {
    const matchStartPosition = known.indexOf(input);
    const matches: PathMatchPart[] = [];

    if (matchStartPosition === -1 || input.length === 0) {
      matches.push({
        isMatch: false,
        substring: known,
      });
      return matches;
    }

    // Pre-match segment if not at start
    if (matchStartPosition > 0) {
      matches.push({
        isMatch: false,
        substring: known.slice(0, matchStartPosition),
      });
    }

    // Matching segment
    if (input.length > 0) {
      matches.push({
        isMatch: true,
        substring: input,
      });
    }

    // Post-match segment if any remains
    const remainderStart = matchStartPosition + input.length;
    if (remainderStart < known.length) {
      matches.push({
        isMatch: false,
        substring: known.slice(remainderStart),
      });
    }

    return matches;
  }
}

export const pathMatcher = new PathMatcher();
